import React from 'react';
import type { Task } from '@/app-package';
import { GetPermissionsAppointmentDTO } from '@bb/domain/dist/types/dto/AppointmentController/getPermissionsAppointment';
import { NoTasksSVG } from '@bb/elements-web';
import { Divider, Icon } from 'components';
import { featureEnabled } from 'utils';
import { EmptyContainer, EmptySection } from '../../components';
import AppointmentRequestedItem from './AppointmentRequestedItem';
import OfferFormSubmittedItem from './OfferFormSubmittedItem';
import OfferRegisteredItem from './OfferRegisteredItem';
import RejectedDocumentItem from './RejectedDocumentItem';
import AssignLockboxTasksSection from './AssignLockboxTasksSection';

type AllTaskSectionProps = {
  items: Task[];
  permissionsMap: Record<string, GetPermissionsAppointmentDTO>;
  refetchTasks: () => void;
  team?: boolean;
};

const AllTaskSection: React.FC<AllTaskSectionProps> = ({
  items,
  permissionsMap,
  refetchTasks,
  team,
}) => {
  if (!items.length) {
    if (featureEnabled('showingsMadeEasyFeature') && !team) {
      return (
        <EmptySection
          dataCy="tasks-empty"
          Image={<NoTasksSVG width={133} height={120} />}
          mainText="No tasks yet"
          secondaryText="View incoming tasks that require your attention here."
        />
      );
    }
    // Team dashboard will continue to use old empty design for now
    return (
      <EmptyContainer data-cy="tasks-empty">
        <Icon type="inboxFill" width={40} color="gray6" offset={[0, 4]} />
        <span>No tasks yet</span>
      </EmptyContainer>
    );
  }

  const assignLockboxTasks = items.filter(
    item => item.taskType === 'unconfigured',
  );
  const otherTasks = items.filter(item => item.taskType !== 'unconfigured');

  return (
    <>
      <AssignLockboxTasksSection tasks={assignLockboxTasks} />

      {otherTasks.map((item, index) => {
        let element: React.ReactElement;
        switch (item.taskType) {
          case 'appointment':
            element = (
              <AppointmentRequestedItem
                key={index}
                itemData={item.data}
                permission={permissionsMap[item.data.appointmentId]}
                team={team}
              />
            );
            break;
          case 'offer801Verification': {
            element = (
              <OfferFormSubmittedItem key={index} itemData={item.data} />
            );
            break;
          }
          case 'offerNotification': {
            element = <OfferRegisteredItem key={index} itemData={item.data} />;
            break;
          }
          case 'rejectedDocument': {
            element = <RejectedDocumentItem key={index} itemData={item.data} />;
            break;
          }
          default: {
            element = null;
            break;
          }
        }

        if (!element && index === items.length - 1) {
          return element;
        }

        return (
          <React.Fragment key={index}>
            {element} <Divider style={{ margin: 0 }} />
          </React.Fragment>
        );
      })}
    </>
  );
};

export default AllTaskSection;
